/**
 * <AddToPriceListButton> — small inline action that appears next to a line
 * item in Enterprise / Sales-Invoice forms when the picked product is NOT
 * yet in the selected client's price list.  Tapping it opens a tiny modal
 * (rate / unit / discount %) and PATCHes the client's price list via
 * `PUT /api/clients/{id}/price-list` (full-replace endpoint).
 *
 * Props:
 *   clientId       : id of the selected debtor (skip render if blank)
 *   product        : { product_id, product_name, item_type, unit, rate }
 *   currentList    : existing price_list array (so we can append + replace)
 *   onAdded        : callback after successful save — receives the new entry
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AddToPriceListButton({ clientId, product, currentList, onAdded }) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(product?.rate || 0);
  const [unit, setUnit] = useState(product?.unit || 'kg');
  const [discount, setDiscount] = useState(0);
  const [saving, setSaving] = useState(false);

  if (!clientId || !product?.product_id) return null;
  // Already in price list → nothing to add
  if ((currentList || []).some(p => p.product_id === product.product_id)) return null;

  const save = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!parseFloat(rate)) { toast.error('Enter a rate'); return; }
    setSaving(true);
    try {
      const newEntry = {
        product_id:   product.product_id,
        product_name: product.product_name,
        item_type:    product.item_type || 'finished',
        unit:         unit || 'KG',
        rate:         parseFloat(rate) || 0,
        discount_pct: parseFloat(discount) || 0,
        markup_pct:   0,
        notes:        '',
      };
      const merged = [...(currentList || []), newEntry];
      await axios.put(`${API}/clients/${clientId}/price-list`, { price_list: merged }, { withCredentials: true });
      toast.success(`${product.product_name} added to price list`);
      setOpen(false);
      onAdded?.(newEntry);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally { setSaving(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); setRate(product?.rate || 0); setUnit(product?.unit || 'kg'); }}
        title="Add this item to the client's price list"
        data-testid={`add-to-price-list-${product.product_id}`}
        className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40 flex items-center gap-0.5 whitespace-nowrap"
      >
        <Plus size={9} /> Add to price list
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 no-print" onClick={() => setOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-zinc-100 text-sm font-semibold">Add to Price List</div>
                <div className="text-zinc-500 text-xs mt-0.5 truncate">{product.product_name}</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200" data-testid="cancel-add-price">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-2.5">
              <div>
                <label className="block text-zinc-400 text-xs mb-1">Rate (₹)</label>
                <input type="number" min="0" step="any" value={rate} onChange={e => setRate(e.target.value)}
                  data-testid="add-price-rate"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="block text-zinc-400 text-xs mb-1">Unit</label>
                <input type="text" value={unit} onChange={e => setUnit(e.target.value)}
                  data-testid="add-price-unit"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="block text-zinc-400 text-xs mb-1">Discount % (optional)</label>
                <input type="number" min="0" max="100" step="any" value={discount} onChange={e => setDiscount(e.target.value)}
                  data-testid="add-price-discount"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="flex-1 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm" data-testid="add-price-cancel">
                Cancel
              </button>
              <button onClick={save} disabled={saving} className="flex-1 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold disabled:opacity-50" data-testid="add-price-save">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
